import * as React from "react";
import type { ColumnDef } from "@tanstack/react-table";

import type { ManagedUser } from "api/users";
import {
  Avatar,
  Badge,
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconButton,
  Pagination,
  type BadgeProps,
} from "Components/ui";
import { History, KeyRound, Lock, MoreHorizontal, Trash2, Unlock } from "Components/ui/icons";
import { displayName, getRoleName, getUserId, type UserPermissions } from "./data";

const PAGE_SIZE = 20;

const roleTone = (roleName: string): NonNullable<BadgeProps["tone"]> => {
  const lowered = roleName.toLowerCase();
  if (lowered.includes("super")) {
    return "primary";
  }
  return lowered.includes("admin") ? "info" : "neutral";
};

export interface UserRowActions {
  onEdit: (user: ManagedUser) => void;
  onResetPassword: (user: ManagedUser) => void;
  onLoginHistory: (user: ManagedUser) => void;
  onToggleActive: (user: ManagedUser) => void;
  onDelete: (user: ManagedUser) => void;
}

interface UserTableProps extends UserRowActions {
  /** Already filtered by the search. */
  users: ManagedUser[];
  loading: boolean;
  permissions: Pick<UserPermissions, "canWrite" | "canApprove" | "canDelete" | "canConfigure">;
  /** The table goes back to page 1 when this changes. */
  searchQuery: string;
  /** Shown when there are no rows. */
  empty: React.ReactNode;
}

/** Users table with row actions, paged 20 at a time on the client. */
const UserTable: React.FC<UserTableProps> = ({
  users,
  loading,
  permissions,
  searchQuery,
  empty,
  onEdit,
  onResetPassword,
  onLoginHistory,
  onToggleActive,
  onDelete,
}) => {
  const { canWrite, canApprove, canDelete, canConfigure } = permissions;
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const total = users.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = React.useMemo(
    () => users.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [users, safePage]
  );

  const columns = React.useMemo<ColumnDef<ManagedUser>[]>(
    () => [
      {
        id: "user",
        header: "User",
        meta: { className: "min-w-[15rem]" },
        cell: ({ row }) => {
          const user = row.original;
          return (
            <div className="flex min-w-0 items-center gap-3 py-2">
              <Avatar name={user.name || user.userName || "?"} />
              <div className="min-w-0">
                <p className="truncate font-medium text-foreground">{user.name || "Unnamed"}</p>
                <p className="truncate text-xs text-muted-foreground">{user.email || "No email"}</p>
              </div>
            </div>
          );
        },
      },
      {
        id: "userName",
        header: "Username",
        cell: ({ row }) => {
          const userName = (row.original.userName || "").replace(/^@/, "");
          return userName ? (
            <span className="text-muted-foreground">@{userName}</span>
          ) : (
            <span className="text-subtle-foreground">None</span>
          );
        },
      },
      {
        id: "phone",
        header: "WhatsApp",
        cell: ({ row }) =>
          row.original.phoneNumber ? (
            <span className="whitespace-nowrap tabular-nums text-muted-foreground">
              {row.original.phoneNumber}
            </span>
          ) : (
            <span className="text-subtle-foreground">Not set</span>
          ),
      },
      {
        id: "role",
        header: "Role",
        cell: ({ row }) => {
          const roleName = getRoleName(row.original);
          return roleName ? (
            <Badge tone={roleTone(roleName)}>{roleName}</Badge>
          ) : (
            <span className="text-subtle-foreground">No role</span>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) =>
          row.original.isActive ? (
            <Badge tone="success" dot>
              Active
            </Badge>
          ) : (
            <Badge tone="neutral" dot>
              Suspended
            </Badge>
          ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        meta: { align: "right" },
        cell: ({ row }) => {
          const user = row.original;
          const name = displayName(user);
          return (
            <div className="flex items-center justify-end gap-1">
              {canWrite && (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Edit ${name}`}
                  onClick={() => onEdit(user)}
                >
                  Edit
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton label={`More for ${name}`} icon={<MoreHorizontal />} size="sm" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem icon={<History />} onSelect={() => onLoginHistory(user)}>
                    Login history
                  </DropdownMenuItem>
                  {canConfigure && (
                    <DropdownMenuItem icon={<KeyRound />} onSelect={() => onResetPassword(user)}>
                      Reset password
                    </DropdownMenuItem>
                  )}
                  {canApprove && (
                    <DropdownMenuItem
                      icon={user.isActive ? <Lock /> : <Unlock />}
                      onSelect={() => onToggleActive(user)}
                    >
                      {user.isActive ? "Suspend" : "Activate"}
                    </DropdownMenuItem>
                  )}
                  {canDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        tone="danger"
                        icon={<Trash2 />}
                        onSelect={() => onDelete(user)}
                      >
                        Delete user
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    [
      canApprove,
      canConfigure,
      canDelete,
      canWrite,
      onDelete,
      onEdit,
      onLoginHistory,
      onResetPassword,
      onToggleActive,
    ]
  );

  return (
    <DataTable
      columns={columns}
      data={pageItems}
      loading={loading}
      // Sorting would only reorder the current page, so the list keeps the server's order.
      enableSorting={false}
      caption="Users"
      getRowId={(user, index) => getUserId(user) || `row-${index}`}
      empty={empty}
      footer={
        !loading && total > 0 ? (
          <Pagination
            page={safePage}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={total}
            pageSize={PAGE_SIZE}
            noun="users"
          />
        ) : undefined
      }
    />
  );
};

export default UserTable;
